/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { IconButton, InputAdornment, Menu, MenuItem, styled } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField, TimeFieldProps } from "@mui/x-date-pickers/TimeField";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useRef, useState } from "react";

const StyledTimeField = styled(TimeField<Dayjs>)<{ error?: boolean }>(({ theme, error }) => ({
  width: "100%",
  [theme.breakpoints.up("lg")]: {
    minWidth: "140px",
  },
  "& .MuiOutlinedInput-root": {
    height: "3rem",
  },
  ":focused": {
    ...(error && {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `2px solid ${theme.colors.error700}`,
      },
    }),
  },
  ...(error && {
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${theme.colors.error700}`,
    },
  }),
}));

const inputStyle: React.CSSProperties = {
  padding: 0,
};

const DEFAULT_STEP_MINUTES = 30;

const isSameTimeOfDay = (a: Dayjs, b: Dayjs): boolean =>
  a.hour() === b.hour() && a.minute() === b.minute();

const buildTimeOptions = (stepMinutes: number): Dayjs[] => {
  const start = dayjs().startOf("day");
  const optionCount = (24 * 60) / stepMinutes;
  return Array.from({ length: optionCount }, (_, index) =>
    start.add(index * stepMinutes, "minute")
  );
};

export interface TimePickerProps {
  error?: boolean;
  timeSteps?: { minutes?: number };
}

type Props = TimePickerProps &
  Omit<TimeFieldProps<Dayjs>, "onChange"> & {
    onChange?: (value: Dayjs | null) => void;
  };

export const TimePicker = ({ error, value, onChange, timeSteps, disabled, ...props }: Props) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const timeOptions = useMemo(
    () => buildTimeOptions(timeSteps?.minutes ?? DEFAULT_STEP_MINUTES),
    [timeSteps?.minutes]
  );

  const handleSelect = (option: Dayjs): void => {
    const anchor = value ?? dayjs();
    onChange?.(anchor.hour(option.hour()).minute(option.minute()).second(0).millisecond(0));
    setOpen(false);
  };

  const scrollSelectedIntoView = (node: HTMLLIElement | null) => {
    node?.scrollIntoView({ block: "center" });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledTimeField
        ref={anchorRef}
        error={error}
        value={value}
        onChange={onChange}
        disabled={disabled}
        slotProps={{
          textField: {
            inputProps: {
              style: inputStyle,
            },
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={disabled}
                    onClick={() => setOpen(true)}
                    aria-label="Show time options"
                    sx={{ padding: 0 }}
                  >
                    {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          },
        }}
        {...props}
      />
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: {
              maxHeight: "18.75rem",
              width: anchorRef.current?.offsetWidth,
              zIndex: 9999,
            },
          },
        }}
      >
        {timeOptions.map((option) => {
          const selected = value ? isSameTimeOfDay(option, value) : false;
          return (
            <MenuItem
              key={option.format("HH:mm")}
              ref={selected ? scrollSelectedIntoView : undefined}
              selected={selected}
              onClick={() => handleSelect(option)}
            >
              {option.format("h:mm A")}
            </MenuItem>
          );
        })}
      </Menu>
    </LocalizationProvider>
  );
};
